import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type Sandbox, SandboxSchema, type SandboxTool } from '@/proto/agentplatform/v1/sandbox_pb';
import {
  ListSandboxesResponseSchema,
  CreateSandboxRequestSchema,
  CreateSandboxResponseSchema,
  GetSandboxRequestSchema,
  GetSandboxResponseSchema,
  UpdateSandboxRequestSchema,
  UpdateSandboxResponseSchema,
  DeleteSandboxRequestSchema,
  DeleteSandboxResponseSchema,
  ListSandboxToolsRequestSchema,
  ListSandboxToolsResponseSchema,
} from '@/proto/agentplatform/httpv2/sandbox_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface SandboxListResult {
  data: Sandbox[];
  total: number;
}

export interface SandboxToolListResult {
  data: SandboxTool[];
}

export type SandboxTraceStatus = 'RUNNING' | 'FINISHED' | 'FAILED' | 'UNKNOWN';

/** 沙箱运行记录 */
export interface SandboxTraceRecord {
  traceId: string;
  sandboxId: string;
  sandboxClassId: string;
  userId: string;
  /** Unix 纳秒 */
  startTimeNs?: bigint;
  /** Unix 纳秒；运行中为空 */
  endTimeNs?: bigint;
  durationMs: number;
  status: SandboxTraceStatus;
  statusMessage: string;
}

export interface ListSandboxTraceRecordsParams {
  pageSize: number;
  /** 分页游标，为空表示第一页 */
  cursor?: string;
  /** 是否返回近似总数 */
  withTotal?: boolean;
  sandboxId?: string;
  fromTimestamp?: string;
  toTimestamp?: string;
}

export interface SandboxTraceRecordListResult {
  items: SandboxTraceRecord[];
  nextCursor: string;
  hasMore: boolean;
  /** 仅 withTotal=true 时有值 */
  total?: number;
}

type JsonObject = Record<string, unknown>;

const text = (value: unknown) => (typeof value === 'string' ? value : '');

function nanos(value: unknown): bigint | undefined {
  if (value === undefined || value === null || value === '' || value === '0' || value === 0) {
    return undefined;
  }
  try {
    return BigInt(value as string | number);
  } catch {
    return undefined;
  }
}

// 枚举兼容名字与数字两种形式
const STATUS_BY_NUMBER: Record<number, SandboxTraceStatus> = { 1: 'RUNNING', 2: 'FINISHED', 3: 'FAILED' };

function traceStatus(value: unknown): SandboxTraceStatus {
  if (typeof value === 'number') return STATUS_BY_NUMBER[value] ?? 'UNKNOWN';
  const name = text(value).replace('TRACE_RECORD_STATUS_', '');
  return name === 'RUNNING' || name === 'FINISHED' || name === 'FAILED' ? name : 'UNKNOWN';
}

function traceRecord(raw: JsonObject): SandboxTraceRecord {
  return {
    traceId: text(raw.trace_id),
    sandboxId: text(raw.sandbox_id),
    sandboxClassId: text(raw.sandbox_class_id),
    userId: text(raw.user_id),
    startTimeNs: nanos(raw.start_time),
    endTimeNs: nanos(raw.end_time),
    durationMs: Number(raw.duration ?? 0) || 0,
    status: traceStatus(raw.status),
    statusMessage: text(raw.status_message),
  };
}

export const sandboxApi = {
  listSandboxes: async (params: ListRequest): Promise<SandboxListResult> => {
    const req = create(ListRequestSchema, {
      page: params.page,
      pageSize: params.pageSize,
      filter: params.filter,
      fuzzy: params.fuzzy,
      orderBy: params.orderBy,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxes',
      toJson(ListRequestSchema, req)
    );
    const resp = fromJson(ListSandboxesResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getSandbox: async (id: string): Promise<Sandbox> => {
    const req = create(GetSandboxRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetSandbox',
      toJson(GetSandboxRequestSchema, req)
    );
    const resp = fromJson(GetSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  createSandbox: async (data: Sandbox): Promise<Sandbox> => {
    const req = create(CreateSandboxRequestSchema, { sandbox: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateSandbox',
      toJson(CreateSandboxRequestSchema, req)
    );
    const resp = fromJson(CreateSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  updateSandbox: async (id: string, data: Sandbox): Promise<Sandbox> => {
    const req = create(UpdateSandboxRequestSchema, { id, sandbox: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateSandbox',
      toJson(UpdateSandboxRequestSchema, req)
    );
    const resp = fromJson(UpdateSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  deleteSandbox: async (id: string): Promise<void> => {
    const req = create(DeleteSandboxRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteSandbox',
      toJson(DeleteSandboxRequestSchema, req)
    );
    fromJson(DeleteSandboxResponseSchema, responseObject.data);
  },

  listSandboxTools: async (id: string): Promise<SandboxToolListResult> => {
    const req = create(ListSandboxToolsRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxTools',
      toJson(ListSandboxToolsRequestSchema, req)
    );
    const resp = fromJson(ListSandboxToolsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [] };
  },

  listSandboxTraceRecords: async (
    params: ListSandboxTraceRecordsParams
  ): Promise<SandboxTraceRecordListResult> => {
    const envelope = (
      await apiClient.post<JsonObject>('/v2/ListSandboxTraceRecords', {
        page_size: params.pageSize,
        cursor: params.cursor || undefined,
        with_total: params.withTotal || undefined,
        sandbox_id: params.sandboxId || undefined,
        from_timestamp: params.fromTimestamp || undefined,
        to_timestamp: params.toTimestamp || undefined,
      })
    ).data;
    const raw = (envelope.data ?? {}) as JsonObject;
    return {
      items: ((raw.items ?? []) as JsonObject[]).map(traceRecord),
      nextCursor: text(raw.next_cursor),
      hasMore: Boolean(raw.has_more),
      total: params.withTotal ? Number(raw.total ?? 0) || 0 : undefined,
    };
  },
};
